import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Chip, MenuItem, Select, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { useField } from 'formik'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'

interface Props {
  /** A FieldArray path, e.g. `rows[2].applicableDestinations`. */
  name: string
  label?: string
  options: BasicSelectOption[]
  placeholder: string
  disabled: boolean
}

/**
 * A chip multi-select, matching how the Retool tab renders these two columns. Empty is a valid
 * and common state — the DTO requires the key to be present but accepts an empty array, and most
 * live rows have one — so there is no placeholder-as-error styling here.
 *
 * `BasicCheckboxSelect` is the app's existing multi-select but renders a full checkbox group,
 * which is far too tall for a fee block with six other fields.
 */
const VendorFeeMultiSelectFieldComponent = ({
  name,
  label,
  options,
  placeholder,
  disabled,
}: Props) => {
  const [field, , helpers] = useField<string[]>(name)

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const { value } = event.target

      // MUI can hand back a comma-joined string. `''.split(',')` is `['']`, not `[]`, and a
      // stray empty string would fail the backend's `@IsEnum(…, { each: true })` on save.
      const selected = typeof value === 'string' ? value.split(',') : value

      helpers.setValue(
        selected.filter((entry) => {
          return entry !== ''
        }),
      )
    },
    [helpers],
  )

  return (
    <div>
      {label ? (
        // Same label as BasicTextField, so the field lines up with its neighbours.
        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
          {label}
        </Typography>
      ) : null}
      <Select<string[]>
        multiple
        displayEmpty
        fullWidth
        disabled={disabled}
        name={name}
        value={field.value}
        onChange={handleChange}
        onBlur={field.onBlur}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <span css={tw`text-shades-400`}>{placeholder}</span>
          }

          return (
            <div css={tw`flex flex-wrap gap-1`}>
              {selected.map((entry) => {
                return <Chip key={entry} label={entry} size="small" />
              })}
            </div>
          )
        }}
      >
        {options.map((option) => {
          return (
            <MenuItem key={String(option.value)} value={String(option.value)}>
              {option.label}
            </MenuItem>
          )
        })}
      </Select>
    </div>
  )
}

export const VendorFeeMultiSelectField = memo(VendorFeeMultiSelectFieldComponent)
